import type { PavementSegment, RouteMetricsResult, SyntaxMetricSlimEdge } from '../api';

const NO_DATA_COLOR = '#3a4a5a';

/**
 * Rampa propia de Conectividad — NO reusa `speedColor` (rojo=malo/verde=
 * bueno, semántica de velocidad). El usuario reportó que esa rampa se leía
 * confusa acá: para sintaxis espacial, valor alto (buena integración/
 * conectividad) = rojo, valor bajo = azul — pedido explícito, semántica
 * invertida respecto a Velocidades a propósito.
 */
export function syntaxColor(value: number, min: number, max: number): string {
  const span = max - min || 1;
  const t = Math.max(0, Math.min(1, (value - min) / span));
  const blue: [number, number, number] = [33, 100, 220];
  const red: [number, number, number] = [220, 45, 45];
  const rgb = blue.map((c, i) => Math.round(c + (red[i] - c) * t));
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}

/**
 * Conectividad reusa exactamente la geometría por-tramo ya calculada para
 * Infraestructura (`RouteMetricsResult.pavement.segments`, cada
 * `{canonicalEdgeId, startM, endM}`), solo cambia qué valor colorea cada
 * tramo: en vez de tipo de pavimento, el valor de sintaxis espacial de ese
 * edge. Mismo truco de stops "duros" que ya usa Velocidades — mismo
 * patrón visual estructural, rampa de color propia.
 */
export function buildSyntaxValueByEdge(edges: SyntaxMetricSlimEdge[]): Map<string, number | null> {
  const map = new Map<string, number | null>();
  for (const e of edges) map.set(e.edgeId, e.value);
  return map;
}

export function buildSyntaxGradientStops(
  segments: PavementSegment[],
  totalLengthM: number,
  valueByEdge: Map<string, number | null>,
  range: { min: number; max: number }
): Array<[number, string]> {
  if (!totalLengthM || segments.length === 0) return [];
  const stops: Array<[number, string]> = [];
  for (const seg of segments) {
    const value = valueByEdge.get(seg.canonicalEdgeId);
    const color = value !== null && value !== undefined ? syntaxColor(value, range.min, range.max) : NO_DATA_COLOR;
    const p0 = Math.max(0, Math.min(1, seg.startM / totalLengthM));
    const p1 = Math.max(p0, Math.min(1, seg.endM / totalLengthM));
    stops.push([p0, color]);
    stops.push([Math.max(p0, p1 - 0.00001), color]);
  }
  return stops;
}

export function syntaxStopsForRoute(
  metrics: RouteMetricsResult | null,
  valueByEdge: Map<string, number | null>,
  range: { min: number; max: number }
): Array<[number, string]> {
  if (!metrics) return [];
  return buildSyntaxGradientStops(metrics.pavement.segments, metrics.pavement.totalLengthM, valueByEdge, range);
}
